import { PureQueryOptions } from '@apollo/client';
import { theme, useToast, SendIcon, NotSend, Tooltip } from '@arnold/common';
import styled from '@emotion/styled';
import { FC } from 'react';
import { Button } from 'react-bootstrap';
import { TFunction } from 'react-i18next';
import { ReportPendingApprovalTooltip } from '../../components/ReportPendingApprovalTooltip';
import { IsAccessTokenValidQuery, ReportAccessQuery, SequenceReportAccessesQuery } from '../../generated/hooks';
import { goToTalkResults } from '../../lib/helpers';
import { useAcceptReport, useSuspendReport } from './utils';

type ActionButtonsProps = {
  report:
    | NonNullable<NonNullable<ReportAccessQuery['survey']>['reports']>[0]
    | NonNullable<SequenceReportAccessesQuery['surveyGroup']>['reports'][0];
  reportBasePath: string;
  isOngoing: boolean;
  isReportPendingApproval?: boolean;
  t: TFunction;
  dependantQueries: Array<string | PureQueryOptions>;
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
};

const ActionMenuContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.f};
  float: right;
`;

const StyledTooltip = styled(Tooltip)`
  display: flex !important;
`;

export const ActionButtons: FC<ActionButtonsProps> = ({
  reportBasePath,
  report,
  isOngoing,
  isReportPendingApproval,
  t,
  dependantQueries,
  user,
}) => {
  const { addToast } = useToast();

  const canBeChanged = dependantQueries.length > 0;

  const acceptResults = useAcceptReport(t, addToast, report, dependantQueries, user);
  const suspendReport = useSuspendReport(t, addToast, report, dependantQueries, user);

  const acceptEnabled = !report.accepted || !isOngoing;
  const suspendEnabled = !(report.sent || report.accepted === false);

  return (
    <ActionMenuContainer>
      {!isReportPendingApproval && suspendEnabled && canBeChanged && (
        <StyledTooltip html={t('dontAccept')}>
          <Button
            onClick={() => suspendReport()}
            variant="link"
            bsPrefix="override"
            className="link no-btn font-size-xs"
            data-cy="btn-suspend-report"
            aria-label={t('dontAccept')}
          >
            <NotSend style={{ marginRight: theme.spacing.c }} />
          </Button>
        </StyledTooltip>
      )}
      {!isReportPendingApproval && acceptEnabled && canBeChanged && (
        <StyledTooltip html={t('accept')}>
          <Button
            onClick={() => acceptResults()}
            variant="link"
            bsPrefix="override"
            className="link no-btn font-size-xs"
            data-cy="btn-accept-report"
            aria-label={t('accept')}
          >
            <SendIcon style={{ marginRight: theme.spacing.c }} />
          </Button>
        </StyledTooltip>
      )}

      <ReportPendingApprovalTooltip isPending={isReportPendingApproval}>
        <Button
          onClick={() => {
            if (!isReportPendingApproval) {
              goToTalkResults(reportBasePath, report.accessKey);
            }
          }}
          variant={'outline-primary'}
          size="sm"
          disabled={isReportPendingApproval}
          data-icom={'btn-report'}
          data-cy={'btn-report'}
        >
          {dependantQueries.length > 0 ? t('report') : t('results')}
        </Button>
      </ReportPendingApprovalTooltip>
    </ActionMenuContainer>
  );
};
